import { NavigateFunction } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'

import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
    NotificationCenterLog,
} from '@/entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    endEventLoggerWithoutAuthApiCall,
    startEventLoggerApiCall,
} from '@/entities/NotificationCenter/NotificationCenterService'
import { TransportationOption } from '@/entities/TransportationOption/TransportationOption'
import { getSettings } from '@/helpers/api/settingsApi'
import {
    getProfileOfCurrentUser as getProfileOfCurrentUserApiCall,
    patchEmergencyContact,
    patchPersonGender,
    patchUserPhoneAndEmail,
    patchUserTransportationOptions,
    postUserAddress,
} from '@/helpers/api/userApi'
import { getErrorResponse } from '@/helpers/getErrorResponse'
import { getEventLogTrackerData } from '@/helpers/getEventLogTrackerData'
import { setSentrySessionUser } from '@/helpers/sentry'
import { checkPermission } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { Role, UserResource } from '@/models'
import { Permission } from '@/models/Role'
import {
    AUTH_UNSET_TOKENS,
    CURRENT_USER_EDIT_FAILURE,
    CURRENT_USER_EDIT_REQUEST,
    CURRENT_USER_EDIT_SUCCESS,
    CURRENT_USER_FAILURE,
    CURRENT_USER_REQUEST,
    CURRENT_USER_SUCCESS,
    SET_APP_ERROR,
} from '@/redux/actionTypes'
import { Reducers } from '@/redux/reducers'
import { TypedDispatch } from '@/redux/store'
import { setToast } from '@/redux/toasts/toastsReducer'
import { ToastType } from '@/types'

import { UserPermission } from './userReducer'

const delay = (milliseconds: number): Promise<void> => new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
})

const startUserUpdatedEventLog = async (): Promise<NotificationCenterLog | undefined> => getEventLogTrackerData(await startEventLoggerApiCall(
    NotificationCenterCategory.USER,
    NotificationCenterAction.UPDATED,
    [NotificationCenterActionCategory.UNKNOWN],
))

const completeEditAndRoute = (title: string, navigate: NavigateFunction) => (dispatch: TypedDispatch): void => {
    navigate(`/${trans('nav.profile.link')}`)
    const toast = trans('pages.profile.updateItemSuccess', { item: title })
    dispatch(setToast({ type: ToastType.pass, text: toast }))
}

export const getProfileOfCurrentUser = () => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: CURRENT_USER_REQUEST })

    try {
        const [userData, settingsData] = await Promise.all([
            getProfileOfCurrentUserApiCall(),
            getSettings(),
        ])

        const user: UserResource = userData.data
        const { roles } = user
        setSentrySessionUser(user)

        const permissions: UserPermission[] = roles
            .reduce((total: Permission[], role: Role) => [...total, ...(role.permissions || [])], [])
            .map((permission: Permission) => ({ name: permission.slug, id: permission.id }))
        const { pathname } = document.location
        // Viewer does not have use-employee-app permission, but still needs to be able to reset
        // password or complete registration
        const forgotPassword = pathname.indexOf(trans('nav.forgotPassword.link')) !== -1
        const completeRegistration = pathname.indexOf(trans('nav.completeRegistration.link')) !== -1
        if (! forgotPassword && ! completeRegistration && ! checkPermission(permissions, 'use-employee-app')) {
            dispatch({ type: SET_APP_ERROR, error: { code: 403 } })
        }

        dispatch({
            type: CURRENT_USER_SUCCESS,
            user: userData.data,
            settings: settingsData.data,
            permissions,
        })
    } catch (error) {
        dispatch({ type: CURRENT_USER_FAILURE, error: getErrorResponse(error) || { status: 401 } })
        dispatch({ type: AUTH_UNSET_TOKENS })
    }
}

export const editUserPhone = (userId: string, phone: string, navigate: NavigateFunction) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch({ type: CURRENT_USER_EDIT_REQUEST })

    const eventLogTrackerData = await startUserUpdatedEventLog()

    try {
        const response = await patchUserPhoneAndEmail(userId, phone, null, eventLogTrackerData)
        dispatch({ type: CURRENT_USER_EDIT_SUCCESS, user: response.data })
        dispatch(completeEditAndRoute(trans('pages.profile.phone'), navigate))
    } catch (error) {
        dispatch({ type: CURRENT_USER_EDIT_FAILURE, error: getErrorResponse(error) })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}

export const editUserEmail = (userId: string, email: string, navigate: NavigateFunction) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch({ type: CURRENT_USER_EDIT_REQUEST })

    const eventLogTrackerData = await startUserUpdatedEventLog()

    try {
        const response = await patchUserPhoneAndEmail(userId, null, email, eventLogTrackerData)
        await endEventLoggerWithoutAuthApiCall(eventLogTrackerData)
        dispatch({ type: CURRENT_USER_EDIT_SUCCESS, user: response.data })
        navigate(`/${trans('nav.profile.link')}`)
        const toast = trans('pages.profile.updateEmailSuccess')
        dispatch(setToast({ type: ToastType.pass, text: toast }))
        await delay(3500)
        dispatch(setToast({ type: ToastType.pass, text: trans('pages.profile.logout') }))
        await delay(1500)
        dispatch({ type: AUTH_UNSET_TOKENS })
    } catch (error) {
        dispatch({ type: CURRENT_USER_EDIT_FAILURE, error: getErrorResponse(error) })
    }
}

export const editTransportationOptions = (
    personId: string,
    transportationOptions: TransportationOption[],
    navigate: NavigateFunction,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    dispatch({ type: CURRENT_USER_EDIT_REQUEST })

    const eventLogTrackerData = await startUserUpdatedEventLog()

    try {
        await patchUserTransportationOptions(personId, transportationOptions, eventLogTrackerData)
        const { currentUser } = getState().userReducer
        if (! currentUser) throw new Error('No current user')
        currentUser.transportationOptions = transportationOptions
        dispatch({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        dispatch(completeEditAndRoute(trans('pages.profile.transportationOption'), navigate))
    } catch (error) {
        dispatch({ type: CURRENT_USER_EDIT_FAILURE, error: getErrorResponse(error) })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}

export const editUserAddress = (
    personId: string,
    street: string,
    houseNumber: string,
    houseNumberAddition: string,
    postalCode: string,
    city: string,
    navigate: NavigateFunction,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    dispatch({ type: CURRENT_USER_EDIT_REQUEST })

    const eventLogTrackerData = await startUserUpdatedEventLog()

    try {
        const response = await postUserAddress(
            personId,
            street,
            houseNumber,
            houseNumberAddition,
            postalCode,
            city,
            eventLogTrackerData,
        )
        const { currentUser } = getState().userReducer
        if (! currentUser) throw new Error('No current user')
        currentUser.person.addresses?.push(response.data)
        dispatch({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        dispatch(completeEditAndRoute(trans('pages.profile.address'), navigate))
    } catch (error) {
        dispatch({ type: CURRENT_USER_EDIT_FAILURE, error: getErrorResponse(error) })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}

export const editGender = (personId: string, gender: string, navigate: NavigateFunction) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    dispatch({ type: CURRENT_USER_EDIT_REQUEST })

    const eventLogTrackerData = await startUserUpdatedEventLog()

    try {
        const response = await patchPersonGender(personId, gender, eventLogTrackerData)
        const { currentUser } = getState().userReducer
        if (! currentUser) throw new Error('No current user')
        currentUser.person.gender = response.data.gender
        dispatch({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        dispatch(completeEditAndRoute(trans('common.gender'), navigate))
    } catch (error) {
        dispatch({ type: CURRENT_USER_EDIT_FAILURE, error: getErrorResponse(error) })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}

export const editUserEmergencyContact = (
    personId: string,
    emergencyContactId: string | undefined,
    fullName: string,
    phone: string,
    relationType: string,
    navigate: NavigateFunction,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    dispatch({ type: CURRENT_USER_EDIT_REQUEST })

    const eventLogTrackerData = await startUserUpdatedEventLog()

    try {
        const response = await patchEmergencyContact(
            personId, emergencyContactId, fullName, phone, relationType, eventLogTrackerData,
        )
        const { currentUser } = getState().userReducer
        if (! currentUser) throw new Error('No current user')
        currentUser.person.emergencyContact = response.data

        dispatch({ type: CURRENT_USER_EDIT_SUCCESS, user: currentUser })
        dispatch(completeEditAndRoute(trans('pages.profile.emergencyContact'), navigate))
    } catch (error) {
        dispatch({ type: CURRENT_USER_EDIT_FAILURE, error: getErrorResponse(error) })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}
